import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Form, Input, Typography, Alert, Checkbox, Modal, Radio, message } from 'antd';
import { login, isAuthenticated } from '../services/authService';

const { Title, Text } = Typography;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [custModalOpen, setCustModalOpen] = useState(false);
  const [custList, setCustList] = useState([]);
  const [selectedCustNo, setSelectedCustNo] = useState('');
  const [pendingLogin, setPendingLogin] = useState(null);
  const [pwResetModalOpen, setPwResetModalOpen] = useState(false);
  const [pwResetEmail, setPwResetEmail] = useState('');
  const [pwResetLoading, setPwResetLoading] = useState(false);
  const [pwResetError, setPwResetError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // If already logged in, redirect
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/products/tour', { replace: true });
    }
  }, [navigate]);

  const onFinish = async (values) => {
    setError('');
    setLoading(true);
    try {
      await login(values.userName, values.password, !!values.remember);
      const from = location.state?.from?.pathname || '/products/tour';
      navigate(from, { replace: true });
    } catch (e) {
      if (e?.code === 'MULTI_CUST' && Array.isArray(e?.custList) && e.custList.length > 0) {
        setPendingLogin({
          userName: values.userName,
          password: values.password,
          remember: !!values.remember,
          from: location.state?.from?.pathname || '/products/tour',
        });
        setCustList(e.custList);
        setSelectedCustNo(String(e.custList[0]?.cust_no || ''));
        setCustModalOpen(true);
        setError('');
        return;
      }
      setError(e?.message || '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const openPwResetModal = () => {
    setPwResetEmail('');
    setPwResetError('');
    setPwResetModalOpen(true);
  };

  const handlePwReset = async () => {
    const email = pwResetEmail.trim();
    if (!email) {
      setPwResetError('아이디(이메일)을 입력해주세요.');
      return;
    }

    setPwResetError('');
    setPwResetLoading(true);
    try {
      const url = `https://trpliveapi1.tripbox.co.kr/Users/PasswordResetEmailSend?userID=${encodeURIComponent(email)}`;
      const resp = await fetch(url, {
        method: 'GET',
      });

      const contentType = resp.headers.get('content-type') || '';
      let body;
      if (contentType.includes('application/json')) {
        body = await resp.json();
      } else {
        body = await resp.text();
      }

      if (!resp.ok) {
        throw new Error('비밀번호 초기화 요청에 실패했습니다.');
      }

      if (typeof body === 'string') {
        if (body.trim() === 'success') {
          message.success('임시 비밀번호가 이메일로 발송되었습니다. 이메일을 확인해주세요.');
          setPwResetModalOpen(false);
          setPwResetEmail('');
          return;
        }
        // 문자열이지만 success 가 아닌 경우 일반 오류 처리
        setPwResetError('비밀번호 초기화에 실패했습니다. 다시 시도해주세요.');
        return;
      }

      if (body && typeof body === 'object' && typeof body.message === 'string') {
        if (body.message === '회원 정보가 잘못 되었습니다') {
          setPwResetError('회원 정보가 잘못 되었습니다');
          return;
        }
        setPwResetError(body.message);
        return;
      }

      setPwResetError('비밀번호 초기화에 실패했습니다. 다시 시도해주세요.');
    } catch (e) {
      setPwResetError(e?.message || '비밀번호 초기화에 실패했습니다.');
    } finally {
      setPwResetLoading(false);
    }
  };

  const onConfirmCust = async () => {
    if (!pendingLogin) return;
    if (!selectedCustNo) {
      setError('로그인할 회사를 선택해주세요.');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await login(pendingLogin.userName, pendingLogin.password, pendingLogin.remember, selectedCustNo);
      setCustModalOpen(false);
      setCustList([]);
      setSelectedCustNo('');
      setPendingLogin(null);
      navigate(pendingLogin.from, { replace: true });
    } catch (e) {
      setError(e?.message || '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white p-8">
        <div className="mb-6 text-center">
          <Title level={3} style={{ marginBottom: 0 }}>여행사 업무 시스템 로그인</Title>
          <Text type="secondary">이메일과 비밀번호를 입력하세요</Text>
        </div>

        {error && (
          <Alert message={error} type="error" showIcon className="mb-4" />
        )}

        <Form layout="vertical" onFinish={onFinish} initialValues={{ userName: '', password: '', remember: true }}>
          <Form.Item
            label="아이디 (이메일)"
            name="userName"
            rules={[{ required: true, message: '아이디(이메일)를 입력해주세요.' }]}
          >
            <Input size="large" placeholder="이메일을 입력하세요" />
          </Form.Item>

          <Form.Item
            label="비밀번호"
            name="password"
            rules={[{ required: true, message: '비밀번호를 입력해주세요.' }]}
          >
            <Input.Password size="large" placeholder="비밀번호를 입력하세요" />
          </Form.Item>

          <Form.Item name="remember" valuePropName="checked">
            <div className="flex items-center justify-between">
              <Checkbox>
                로그인 유지
              </Checkbox>
              <Button type="link" onClick={openPwResetModal} style={{ paddingRight: 0 }}>
                비밀번호 찾기
              </Button>
            </div>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={loading}>
              로그인
            </Button>
          </Form.Item>
        
          <Form.Item>
            <Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
              회원가입 문의 : 02-6080-8800
            </Text>
          </Form.Item>
        </Form>

        <Modal
          title="회사 선택"
          open={custModalOpen}
          onOk={onConfirmCust}
          okText="선택"
          cancelText="취소"
          confirmLoading={loading}
          onCancel={() => {
            setCustModalOpen(false);
            setCustList([]);
            setSelectedCustNo('');
            setPendingLogin(null);
          }}
        >
          <Radio.Group
            value={selectedCustNo}
            onChange={(e) => setSelectedCustNo(String(e.target.value))}
            style={{ width: '100%' }}
          >
            {custList.map((c) => (
              <div key={String(c.cust_no)} style={{ padding: '6px 0' }}>
                <Radio value={String(c.cust_no)}>
                  {c.cust_name} ({c.cust_no})
                </Radio>
              </div>
            ))}
          </Radio.Group>
        </Modal>

        <Modal
          title="비밀번호 초기화"
          open={pwResetModalOpen}
          onCancel={() => {
            if (pwResetLoading) return;
            setPwResetModalOpen(false);
            setPwResetEmail('');
            setPwResetError('');
          }}
          footer={null}
        >
          <div style={{ marginBottom: 16, whiteSpace: 'pre-line' }}>
            비밀번호를 잊으셨나요?

            회원 가입하신 이메일 주소를 입력해 주시면

            임시 비밀번호를 이메일로 발급해 드립니다.

            로그인 후 마이페이지에서 비밀번호를 변경 하세요.
          </div>

          <Form layout="vertical" onFinish={handlePwReset}>
            <Form.Item label="아이디 (이메일)">
              <Input
                value={pwResetEmail}
                onChange={(e) => {
                  setPwResetEmail(e.target.value);
                  if (pwResetError) setPwResetError('');
                }}
                placeholder="이메일을 입력하세요"
              />
            </Form.Item>

            {pwResetError && (
              <div style={{ color: '#ff4d4f', marginBottom: 16 }}>{pwResetError}</div>
            )}

            <div style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={pwResetLoading}
              >
                임시 비밀번호 발급
              </Button>
            </div>
          </Form>
        </Modal>

      </div>
    </div>
  );
};

export default Login;
